import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, filter, takeUntil } from 'rxjs';
import {
  TreeNodeType
} from "services/definition/enum";
import {
  ITreeNode
} from "services/definition/interface";
import { Logger } from 'src/app/common/utility/logger/logger';
import { CountryCode } from 'src/app/services/translation/country-code';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { FUNC_TYPE, ListSettings, ListWidgetSettingsComponent } from './list-widget-settings/list-widget-settings.component';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { KeyWord } from 'common/constant/KeyWords';
import { MILL_SECONDS } from 'common/constant/general';
import { NodeTypes } from 'services/definition/class/node-types';
import { DefinitionService } from 'services/definition/definition.service';
import { MessageType } from 'services/socket/model/message-type';
import { Device, SubscriptionRequest } from 'services/subscription/subscription-common';
import { SubscriptionService } from "src/app/services/subscription/subscription.service";

interface ListSubscription {
  sourceId: string
  deviceId: string
  signalId: string
  displayName: string
  ltmi: string
  ltmiRef: any | undefined
  settings: any
  buffer: Array<any>
  lastTimestamp: number
  data: any | undefined
  UoM: string | undefined
  precision: number
}

const LIST_SUBSCRIPTION_DEFAULT = {
  sourceId: "",
  deviceId: "",
  signalId: "",
  displayName: "",
  ltmi: "",
  ltmiRef: undefined,
  settings: undefined,
  buffer: [],
  lastTimestamp: 0,
  data: undefined
}


@Component({
  selector: 'list-widget',
  templateUrl: './list-widget.component.html',
  styleUrls: ['./list-widget.component.scss']
})
export class ListWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input() listSettings!: ListSettings;
  @Output() listSettingsChange = new EventEmitter<ListSettings>
  @Input() editMode!: boolean;
  @Output() deleteWidgetChange = new EventEmitter<boolean>()
  private unsubscribe: Subject<void> = new Subject();
  private languageCode: CountryCode | undefined = undefined;
  private subscriptions: Array<SubscriptionRequest> = []
  public listSubscriptions: Array<ListSubscription> = [];
  public titleStyle!: string

  // Drag cursor style
  public get cursorStyle(): string {
    return this.editMode ? "cursor: move;" : ""
  } // get cursorStyle()

  public listHeight(): string {
    if (this.editMode)
      return "height: 94%;";
    // TODO: Add check if Widget Title, then do different heights
    return "height: 97%;";
  }

  public fontHeight(): number {
    return 14;
  }

  public fontStyle(): string {
    return "font-size: " + this.fontHeight() + "px;";
  }

  constructor(
    private dialog: MatDialog,
    private translationService: TranslationService,
    private subscriptionService: SubscriptionService
  ) { }

  // Update List Order
  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.listSubscriptions, event.previousIndex, event.currentIndex);
    // Reassign the order
    this.listSubscriptions.forEach((item, pos) => {
      this.listSubscriptions[pos].settings.order = pos;
      if (this.listSettings && this.listSettings.sourceNodes) {
        this.listSettings.sourceNodes.forEach(source => {
          this.updateListSettingPosition(this.listSubscriptions[pos], source)
        })
      }
    });
  } // drop

  private updateListSettingPosition(item: ListSubscription, nodes: ITreeNode): void {
    if (nodes.children && nodes.children.length == 0)
      return

    nodes.children?.forEach(node => {
      switch (NodeTypes.getNodeType(node)) {
        case TreeNodeType.Source:
        case TreeNodeType.Group:
          if (node.children) {
            this.updateListSettingPosition(item, node)
          }
          break;
        case TreeNodeType.Device:
          if (node.name !== item.deviceId)
            return
          // Find the signal to update
          let found = node.children.find((child: any) => child.name === item.signalId)
          if (found)
            found.settings.order = item.settings.order
          break;
      } // switch NodeType
    })

  } // updateListSettingPosition()

  private sortSubscriptions(): void {
    this.listSubscriptions.sort((a, b) => a.settings.order > b.settings.order ? 1 : -1)
  } // sortSubscriptions()

  public ngOnInit(): void {

    this.loadTranslations();

    // Setup Listener for Telemetry Messages
    this.subscriptionService.listenForTelementryMessages().pipe(takeUntil(this.unsubscribe),
      filter((message: any) => message.eventType === MessageType.Telemetry)).
      subscribe((message: any) => {
        this.listSubscriptions.forEach(signal => {
          if ((signal.sourceId === message.sourceId)
            && (signal.deviceId === message.deviceId)
            && (message[signal.signalId] || message[signal.signalId] === 0)
          ) {
            switch (signal.settings?.function) {
              case FUNC_TYPE.AVERAGE:
                if (signal.data == undefined) {
                  signal.data = <number>message[signal.signalId].toFixed(signal.precision)
                }
                signal.buffer.push({ x: message[KeyWord.telemetryDDBsortKey], y: <number>message[signal.signalId] });
                let currentTime = Date.now()
                signal.buffer.sort((a, b) => a.x > b.x ? 1 : -1)
                // Is the Oldest data older than interval?
                if ((signal.buffer.length)
                  && ((currentTime - signal.buffer[0].x) >= (signal.settings.functionTimeframe * MILL_SECONDS))) {
                  // Perform the average
                  let sum = 0;
                  signal.buffer.forEach(val => sum += val.y)
                  signal.data = (sum / signal.buffer.length).toFixed(signal.precision)
                  signal.buffer = [];
                } // if signal.buffer.length
                break;
              case FUNC_TYPE.ENUM:
                if (signal.lastTimestamp < message[KeyWord.telemetryDDBsortKey]) {
                  signal.lastTimestamp = message[KeyWord.telemetryDDBsortKey];
                  signal.data = this.getEnumDisplayName(message[signal.signalId], signal.ltmiRef.complexSchema)
                }
                break;
              case FUNC_TYPE.REALTIME:
              default:
                if (signal.lastTimestamp < message[KeyWord.telemetryDDBsortKey]) {
                  signal.lastTimestamp = message[KeyWord.telemetryDDBsortKey];
                  signal.data = message[signal.signalId].toFixed(signal.precision)
                }
                break;
            }
          } // telemetrySubscriptions.forEach()
        }) // forEach Subscription
      }); // socksetService.listen()

    this.titleStyle = "display: flex; flex-direction: column; " +
      "font-weight: " + this.listSettings.title.font.weight + ";" +
      "align-items: " + this.listSettings.title.align + ";" +
      "font-size: " + this.listSettings.title.font.size + "px;"

    this.listSubscriptions = [];
    this.subscriptions = []

    // TODO: Configure what to display
    this.buildSubscriptions(<any>this.listSettings.sourceNodes);
    this.sortSubscriptions();
  } // ngOnInit()

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  } // ngOnDestroy()

  public ngOnChanges(changes: SimpleChanges): void {
    const listSettings = 'listSettings';

    if (!!changes[listSettings]) {
      const change = changes[listSettings];
      // If previous isn't undefined
      if (change.isFirstChange() || (change.previousValue !== change.currentValue)) {
        // Build the Line Chart lines
        this.listSubscriptions = [];
        this.subscriptions = []

        // TODO: Configure what to display
        this.buildSubscriptions(<any>this.listSettings.sourceNodes);
        this.sortSubscriptions();
        this.subscriptionService.appendSubscription(this.subscriptions);
        if (change.isFirstChange()) {
          this.subscriptionService.sendSubscriptionRequest();
        } else {
          this.subscriptionService.sendSubscription()
        }

      } // If previous!= current
    }
  } // ngOnChanges()

  // Assumes the nodes are already pruned
  private arraySignalKey: Array<string> = []
  private sourceId!: string;
  private sourceDisplayName!: string;
  private deviceId!: string;
  private deviceDisplayName!: string
  private buildSubscriptions(nodes: Array<any>): void {

    nodes.forEach((node) => {
      switch (NodeTypes.getNodeType(node)) {
        case TreeNodeType.Source:
          this.arraySignalKey = []
          this.sourceId = node.name;
          this.sourceDisplayName = node.displayName

          if (node.children) { // If no, then mis-configured tree
            this.buildSubscriptions(node.children)
          }
          break
        case TreeNodeType.ArrayNode:
          if (node.children) {
            this.buildSubscriptions(node.children)
          }
          break;
        case TreeNodeType.ObjectNode:
          this.arraySignalKey.push(node.name)
          if (node.children) {
            this.buildSubscriptions(node.children)
            this.arraySignalKey.pop()
          }
          break
        case TreeNodeType.Group:
          if (node.children) {
            this.buildSubscriptions(node.children);
          }
          break
        case TreeNodeType.Device:
          this.arraySignalKey = []
          this.deviceId = node.name
          this.deviceDisplayName = node.displayName

          if (node.children) {
            this.buildSubscriptions(node.children);
          }
          break;
        case TreeNodeType.Telemetry:
          const key = (this.arraySignalKey.length ? (this.arraySignalKey.join("")) : '') + node.name

          let displayName = ""
          if (node.settings.showSourceName) {
            displayName = this.sourceDisplayName + "->"
          }
          if (node.settings.showDeviceName) {
            displayName += this.deviceDisplayName + "->"
          }

          displayName += node.settings.showCustomName ? node.settings.customName : node.displayName

          const sub: ListSubscription = {
            sourceId: this.sourceId,
            deviceId: this.deviceId,
            signalId: key,
            displayName: displayName,
            ltmi: node.ltmi,
            ltmiRef: node.ltmiRef,
            settings: node.settings,
            buffer: [],
            lastTimestamp: 0,
            data: undefined,
            UoM: node.settings.showUoMSymbol ? " " +
              DefinitionService.getUoMSymbol(node.ltmiRef.semanticType, node.ltmiRef.unit) : undefined,
            precision: node.settings.precision
          }

          this.listSubscriptions.push(sub)
          this.appendSubscriptionRequest(sub)
          break;
        default: // Something is wrong
          Logger.error(`WidgetSettings-getSubscriptions() - unknown type: ${node}`)
          break;
      }
    })
  } // buildSubscriptions()

  private appendSubscriptionRequest(sub: ListSubscription): void {
    const sourceFound = this.subscriptions.find((item: SubscriptionRequest) => item.sourceId == sub.sourceId)
    if (sourceFound != undefined) {
      const deviceFound = sourceFound.devices.find((item: Device) => item.deviceId == sub.deviceId)
      if (deviceFound != undefined) {
        // Add signal
        deviceFound.signals.push(sub.signalId)
        return
      }
      // Add device to subscription
      sourceFound.devices.push({
        deviceId: sub.deviceId,
        signals: [sub.signalId]
      })
      return
    }

    const device: Device = {
      deviceId: sub.deviceId,
      signals: [sub.signalId]
    }
    // Add source to subscription
    const request: SubscriptionRequest = {
      sourceId: sub.sourceId,
      devices: [device]
    }

    this.subscriptions.push(request)
  } // appendSubscriptionRequest()

  private getEnumDisplayName(id: number, enums: any): string {
    if (id == undefined || enums == undefined || enums[id] == undefined)
      return "Unknown Enum"
    return enums[id].displayName[TranslationService.CurrentLanguage.CountryCode] ?? enums[id].displayName['en']
  }

  // Translation code
  private labelLast: string | undefined;
  private loadTranslations(): void {
    const label_last: string = 'widgets.list-widget.last';
    const labels: Array<string> = [
      label_last
    ];

    this.translationService.get(labels)
      .pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(translations => {
        this.labelLast = translations[label_last];
      });
  } // loadTranslations()

  private watchLanguageChanges(): void {
    this.translationService.LanguageChange.pipe(
      takeUntil(this.unsubscribe),
      filter((languageCode: CountryCode) => {
        Logger.debug(`Language Filter CountryCode: ${languageCode}.Current: '${this.languageCode}'`)
        return languageCode !== this.languageCode
      })
    ).subscribe((languageCode: CountryCode) => {
      this.languageCode = languageCode
      this.loadTranslations();
    })
  } // watchLanguageChanges()

  public changeSettings(): void {
    const settingsDialog = this.dialog.open(ListWidgetSettingsComponent, { disableClose: true, data: { settings: this.listSettings } });

    // Subscribe to the result
    settingsDialog.afterClosed().subscribe((res) => {
      if (res && res.event === 'apply') {
        this.listSettings = structuredClone(res.data.settings)
        this.listSettingsChange.emit(this.listSettings)

        this.titleStyle = "display: flex; flex-direction: column; " +
          "font-weight: " + this.listSettings.title.font.weight + ";" +
          "align-items: " + this.listSettings.title.align + ";" +
          "font-size: " + this.listSettings.title.font.size + "px;"

        // Build the Line Chart lines
        this.listSubscriptions = [];
        this.subscriptions = []

        // TODO: Configure what to display
        this.buildSubscriptions(<any>this.listSettings.sourceNodes);
        this.sortSubscriptions();
        this.subscriptionService.appendSubscription(this.subscriptions);
        this.subscriptionService.sendSubscription()
      }
    });
  } // changeSettings()

  public deleteWidget(): void {
    this.deleteWidgetChange.emit(true)
  } // deleteWidget()

  public getTitle(): string {
    return this.listSettings.title.showTitle ? this.listSettings.title.text : ""
  } // getTitle()

} // class LineWidgetComponent
